import { useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Logo } from "@/components/Logo";
import { CheckCircle2, Loader2, Zap } from "lucide-react";
import { toast } from "sonner";

const empty = { party_name: "", party_number: "", party_email: "", from_location: "", to_location: "", remarks: "" };

export default function QuickLead() {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.party_name.trim()) return toast.error("Party Name is required");
    if (!form.party_number.trim()) return toast.error("Party Number is required");
    if (form.party_email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.party_email.trim())) return toast.error("Enter a valid email address");
    setLoading(true);
    try {
      const { data } = await api.post("/leads/quick", form);
      setDone(data);
      toast.success(`Lead captured · ${data.order_number}`);
    } catch (err) { toast.error(apiErr(err)); }
    finally { setLoading(false); }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <Header />
        <div className="flex-1 grid place-items-center px-5 py-12">
          <div className="w-full max-w-md border border-border bg-card p-8 text-center" data-testid="quick-lead-success">
            <div className="h-14 w-14 bg-emerald-500/15 text-emerald-500 grid place-items-center mx-auto mb-5"><CheckCircle2 className="h-8 w-8" /></div>
            <h1 className="font-heading font-black text-2xl tracking-tight">Lead Captured</h1>
            <p className="text-muted-foreground mt-2 text-sm">It's now in the Leads dashboard for the team to action.</p>
            <div className="mt-5 border border-border bg-muted p-4">
              <div className="text-xs uppercase tracking-widest text-muted-foreground">Order Number</div>
              <div className="font-mono-num text-2xl font-bold text-primary mt-1" data-testid="ql-order-number">{done.order_number}</div>
            </div>
            <Button className="mt-6 w-full rounded-none font-bold uppercase tracking-wide" onClick={() => { setDone(null); setForm(empty); }} data-testid="ql-new">Add another lead</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      <div className="flex-1 w-full max-w-md mx-auto px-5 py-8">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-primary mb-2"><Zap className="h-4 w-4" /> Quick Lead</div>
        <h1 className="font-heading font-black text-3xl tracking-tight leading-tight">Capture a lead</h1>
        <p className="text-muted-foreground mt-2 text-sm">Six quick fields — takes under a minute. It routes straight to the Leads dashboard.</p>

        <form onSubmit={submit} className="mt-6 space-y-4" data-testid="quick-lead-form">
          <Field label="Party Name" required>
            <Input className="rounded-none bg-card h-11" value={form.party_name} onChange={(e) => set("party_name", e.target.value)} placeholder="e.g. Rahul Sharma" autoFocus data-testid="ql-party-name" />
          </Field>
          <Field label="Party Number" required>
            <Input type="tel" inputMode="tel" className="rounded-none bg-card h-11" value={form.party_number} onChange={(e) => set("party_number", e.target.value)} placeholder="e.g. 98250 14152" data-testid="ql-party-number" />
          </Field>
          <Field label="Mail Id">
            <Input type="email" inputMode="email" className="rounded-none bg-card h-11" value={form.party_email} onChange={(e) => set("party_email", e.target.value)} placeholder="e.g. rahul@email.com" data-testid="ql-party-email" />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="From">
              <Input className="rounded-none bg-card h-11" value={form.from_location} onChange={(e) => set("from_location", e.target.value)} placeholder="Origin" data-testid="ql-from" />
            </Field>
            <Field label="To">
              <Input className="rounded-none bg-card h-11" value={form.to_location} onChange={(e) => set("to_location", e.target.value)} placeholder="Destination" data-testid="ql-to" />
            </Field>
          </div>
          <Field label="Remarks">
            <Textarea rows={3} className="rounded-none bg-card" value={form.remarks} onChange={(e) => set("remarks", e.target.value)} placeholder="Anything the team should know…" data-testid="ql-remarks" />
          </Field>

          <Button type="submit" disabled={loading} className="w-full h-12 rounded-none font-bold uppercase tracking-wide gap-2" data-testid="ql-submit">
            {loading ? <><Loader2 className="h-5 w-5 animate-spin" /> Submitting…</> : <>Submit Lead</>}
          </Button>
        </form>
      </div>
    </div>
  );
}

const Field = ({ label, required, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs font-bold uppercase tracking-wide">{label}{required && <span className="text-primary ml-0.5">*</span>}</Label>
    {children}
  </div>
);

const Header = () => (
  <header className="border-b border-border">
    <div className="max-w-md mx-auto px-5 h-16 flex items-center justify-between">
      <Logo className="h-9" />
      <ThemeToggle />
    </div>
  </header>
);
